import { useCallback, useEffect, useMemo } from 'react';

/**
 * useClickableInteractivePreset
 * - Menyederhanakan logic clickable-interactive (fill_in_the_blanks)
 * - Sinkronisasi nilai dari Konva (number[]) -> Redux answer (string[] "0"|"1"|"2")
 * - Normalisasi jawaban agar tidak ada nilai invalid saat create/edit
 */
export function useClickableInteractivePreset(params) {
  const {
    type,
    interactiveType,
    clickableType,
    answer,
    isInteractiveEnabled,
    dispatch,
    setField,
    CLICKABLE_LENGTH_BY_TYPE,
  } = params;

  const isClickableInteractiveFillIn = useMemo(() => {
    const isFillInTheBlanks = type === 'fill_in_the_blanks';
    const isClickable = interactiveType === 'clickable';
    return isFillInTheBlanks && isInteractiveEnabled && isClickable;
  }, [type, interactiveType, isInteractiveEnabled]);

  // Clickable (Konva): sync preset number[] -> Redux answer string[] (0/1/2)
  const handleChangeClickablePreset = useCallback((nextPreset) => {
    const expectedLen = CLICKABLE_LENGTH_BY_TYPE[clickableType];
    if (!expectedLen) return;

    const normalized = Array.from({ length: expectedLen }, (_, idx) => {
      const raw = Number(nextPreset?.[idx]);
      const clamped = Number.isInteger(raw) ? Math.min(2, Math.max(0, raw)) : 0;
      return String(clamped);
    });

    dispatch(setField({ field: 'answer', value: normalized }));
  }, [CLICKABLE_LENGTH_BY_TYPE, clickableType, dispatch, setField]);

  // Clickable: pastikan jawaban selalu valid (0/1/2), supaya tidak ada '' / value lain nyangkut.
  useEffect(() => {
    if (!isClickableInteractiveFillIn) return;

    const expectedLen = CLICKABLE_LENGTH_BY_TYPE[clickableType];
    if (!expectedLen) return;

    const current = Array.isArray(answer) ? answer : [];
    // Tunggu sampai hook reset menyamakan panjang array
    if (current.length !== expectedLen) return;

    const normalized = current.map((v) => {
      const str = String(v ?? '').trim();
      return str === '0' || str === '1' || str === '2' ? str : '0';
    });

    const isSame = normalized.every((v, i) => v === current[i]);
    if (!isSame) {
      dispatch(setField({ field: 'answer', value: normalized }));
    }
  }, [
    isClickableInteractiveFillIn,
    clickableType,
    answer,
    dispatch,
    setField,
    CLICKABLE_LENGTH_BY_TYPE,
  ]);

  return {
    isClickableInteractiveFillIn,
    handleChangeClickablePreset,
  };
}
